import React, { useCallback, useEffect, useMemo, useState } from "react";
import HoverPreview from "../../common/HoverPreview/HoverPreview";
import QmsDocumentPreviewPane from "./QmsDocumentPreviewPane";
import QmsVaultPeoplePanel from "./QmsVaultPeoplePanel";
import QmsVaultAssociatedDocsPanel from "./QmsVaultAssociatedDocsPanel";
import QmsVaultCommentsPanel from "./QmsVaultCommentsPanel";
import QmsVaultStaffAvatar from "./QmsVaultStaffAvatar";
import QmsVaultQuizPanel from "./QmsVaultQuizPanel";
import QmsVaultVersionHistoryPanel from "./QmsVaultVersionHistoryPanel";
import QmsVaultAuditLogPanel from "./QmsVaultAuditLogPanel";
import {
  bindVaultDocumentQuiz,
  fetchMyVaultDocumentById,
  removeVaultApprover,
  removeVaultAssociatedDocument,
  removeVaultReviewer,
  saveDocumentComment,
  editDocumentComment,
  deleteDocumentComment,
  saveVaultApprovers,
  saveVaultAssociatedDocuments,
  saveVaultReviewers,
  unbindVaultDocumentQuiz,
  vaultApprovalComplete,
  vaultReviewComplete,
} from "./qmsService";
import {
  apiMessage,
  formatVaultApiError,
  getApiTable,
  isApiSuccess,
} from "./qmsApiUtils";
import { VAULT_WORKSPACE_PANELS } from "./qmsVaultWorkspacePanels";
import {
  getVaultDocumentListId,
  getVaultDocumentTitle,
  getVaultDocumentIdAnchorHtml,
  getVaultStageClass,
  getVaultWorkspaceDocumentDetails,
  hasVaultDetailValue,
} from "./qmsVaultUtils";
import {
  getVaultCommentMentionCandidates,
  getVaultWorkflowActions,
  buildAssociatedDocPreviewRow,
  isAssociatedDocPreview,
  isVaultHistoricalPreview,
  patchVaultWorkspaceAssociatedDocs,
  patchVaultWorkspaceDocumentLog,
} from "./qmsVaultWorkspaceUtils";
import { openAuthModal } from "../../common/authmodal/authmodalservice";

function DetailField({ label, value, children }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{children ?? value ?? "—"}</dd>
    </div>
  );
}

function QmsMyVaultWorkspaceModal({
  open,
  onClose,
  row,
  userData,
  activePanel = "preview",
  onPanelChange,
  onNotify,
  onDocumentUpdated,
}) {
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState("");
  const [workspaceResponse, setWorkspaceResponse] = useState(null);
  const [panelSaving, setPanelSaving] = useState(false);
  const [previewRow, setPreviewRow] = useState(null);
  const [railView, setRailView] = useState("details");

  useEffect(() => {
    if (!open) return;
    if (activePanel === "comments") {
      setRailView("comments");
    }
  }, [open, activePanel]);

  const resolvedPanel =
    activePanel === "details" || activePanel === "comments"
      ? "preview"
      : activePanel;

  const viewingHistoricalVersion = isVaultHistoricalPreview(previewRow, row);
  const viewingAssociatedDocument = isAssociatedDocPreview(previewRow);

  const returnToLatestVersion = useCallback(() => {
    setPreviewRow(null);
    onPanelChange?.("preview");
  }, [onPanelChange]);

  const openAssociatedDocPreview = useCallback(
    (docRow) => {
      setPreviewRow(buildAssociatedDocPreviewRow(docRow));
      onPanelChange?.("preview");
    },
    [onPanelChange],
  );

  const openDocumentPreview = useCallback(() => {
    setPreviewRow(null);
    onPanelChange?.("preview");
  }, [onPanelChange]);
  const workflow = useMemo(
    () => getVaultWorkflowActions(workspaceResponse || {}),
    [workspaceResponse],
  );
  const previewDocumentMetadata = useMemo(() => {
    const info = getApiTable(workspaceResponse || {}, "DocumentInfo")[0] || {};
    return (
      previewRow?.DocumentMetadataJson ||
      info?.DocumentMetadataJson ||
      row?.DocumentMetadataJson ||
      null
    );
  }, [previewRow, workspaceResponse, row]);
  const headerStage = useMemo(() => {
    const stage = String(
      workflow?.stage ||
        getVaultWorkspaceDocumentDetails(workspaceResponse || {}, row).stage ||
        row?.DocumentStage ||
        "",
    ).trim();
    return stage && stage !== "—" ? stage : "";
  }, [workflow?.stage, workspaceResponse, row]);

  const reloadWorkspace = useCallback(async () => {
    if (!row?.DocumentId || !userData?.StaffId) return null;
    setDetailsLoading(true);
    setDetailsError("");
    try {
      const response = await fetchMyVaultDocumentById(userData, row.DocumentId);
      setWorkspaceResponse(response);
      return response;
    } catch (error) {
      setDetailsError(error?.message || "Could not load document details.");
      return null;
    } finally {
      setDetailsLoading(false);
    }
  }, [row?.DocumentId, userData]);

  useEffect(() => {
    if (!open || !row?.DocumentId || !userData?.StaffId) {
      return undefined;
    }

    let cancelled = false;
    setDetailsLoading(true);
    setDetailsError("");
    setWorkspaceResponse(null);
    setPreviewRow(null);

    fetchMyVaultDocumentById(userData, row.DocumentId)
      .then((response) => {
        if (!cancelled) setWorkspaceResponse(response);
      })
      .catch((error) => {
        if (!cancelled) {
          setDetailsError(error?.message || "Could not load document details.");
        }
      })
      .finally(() => {
        if (!cancelled) setDetailsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, row?.DocumentId, userData]);

  useEffect(() => {
    if (!open) {
      setDetailsLoading(false);
      setDetailsError("");
      setWorkspaceResponse(null);
      setPreviewRow(null);
      setPanelSaving(false);
      setRailView("details");
    }
  }, [open]);

  const notifySuccess = useCallback(
    (response, fallbackMessage) => {
      onNotify?.({
        title: "Success",
        message: apiMessage(response) || fallbackMessage,
        type: "success",
      });
    },
    [onNotify],
  );

  const notifyError = useCallback(
    (error, fallbackMessage) => {
      onNotify?.({
        title: "Error",
        message: error?.message || fallbackMessage,
        type: "danger",
      });
    },
    [onNotify],
  );

  const runCommentAction = useCallback(
    async (action, successMessage) => {
      setPanelSaving(true);
      try {
        const response = await action();
        if (!isApiSuccess(response)) {
          throw new Error(formatVaultApiError(apiMessage(response)));
        }
        setWorkspaceResponse((prev) => patchVaultWorkspaceDocumentLog(prev || {}, response));
        notifySuccess(response, successMessage);
        onDocumentUpdated?.();
        return response;
      } catch (error) {
        notifyError(error, "Request failed.");
        throw error;
      } finally {
        setPanelSaving(false);
      }
    },
    [notifyError, notifySuccess, onDocumentUpdated],
  );

  const runAssociatedDocsAction = useCallback(
    async (action, successMessage) => {
      setPanelSaving(true);
      try {
        const response = await action();
        if (!isApiSuccess(response)) {
          throw new Error(formatVaultApiError(apiMessage(response)));
        }
        setWorkspaceResponse((prev) =>
          patchVaultWorkspaceAssociatedDocs(prev || {}, response),
        );
        notifySuccess(response, successMessage);
        onDocumentUpdated?.();
        return response;
      } catch (error) {
        notifyError(error, "Request failed.");
        return null;
      } finally {
        setPanelSaving(false);
      }
    },
    [notifyError, notifySuccess, onDocumentUpdated],
  );

  const runPanelAction = useCallback(
    async (action, successMessage) => {
      setPanelSaving(true);
      try {
        const response = await action();
        if (!isApiSuccess(response)) {
          throw new Error(formatVaultApiError(apiMessage(response)));
        }
        notifySuccess(response, successMessage);
        await reloadWorkspace();
        onDocumentUpdated?.();
        return response;
      } catch (error) {
        notifyError(error, "Request failed.");
        return null;
      } finally {
        setPanelSaving(false);
      }
    },
    [notifyError, notifySuccess, onDocumentUpdated, reloadWorkspace],
  );

  if (!open || !row) return null;

  const renderDetailsPanel = () => {
    if (detailsLoading) {
      return <p className="text-muted mb-0 small">Loading document details…</p>;
    }

    if (detailsError) {
      return <p className="text-danger mb-0 small">{detailsError}</p>;
    }

    const details = getVaultWorkspaceDocumentDetails(workspaceResponse || {}, row);
    const info = (workspaceResponse && getApiTable(workspaceResponse, "DocumentInfo")[0]) || row;

    return (
      <dl className="qms-vault-workspace__details-grid">
        <div className="qms-vault-workspace__details-id">
          <dt>Document ID</dt>
          <dd>
            <HoverPreview
              isHtml
              content={details.documentIdHelpHtml}
              anchorHtml={getVaultDocumentIdAnchorHtml(info)}
              plainTextForPreview={details.documentId}
              previewLength={details.documentId.length + 1}
              appendEllipsis={false}
              maxWidth={280}
              maxHeight={220}
              className="qms-vault-workspace__details-id-hover"
            />
          </dd>
        </div>
        <div>
          <dt>Title</dt>
          <dd>
            <button
              type="button"
              className="report-table__cell-link qms-vault-workspace__details-title-link"
              onClick={openDocumentPreview}
              title="Open document preview"
            >
              {details.displayTitle || "—"}
            </button>
          </dd>
        </div>
        <div className="qms-vault-workspace__details-modified">
          <dt>Last activity</dt>
          <dd>
            <div className="qms-vault-workspace__details-modified-row">
              <QmsVaultStaffAvatar
                name={details.lastActivityBy}
                compact
                title={details.lastActivityBy}
              />
              <span className="qms-vault-workspace__details-modified-date">
                {details.lastActivity}
              </span>
            </div>
          </dd>
        </div>
        <DetailField label="Review date" value={details.reviewDate} />
        {details.showCheckoutDetails ? (
          <>
            {hasVaultDetailValue(details.checkedOutBy) ? (
              <DetailField label="Checked out by" value={details.checkedOutBy} />
            ) : null}
            {hasVaultDetailValue(details.checkedOutOn) ? (
              <DetailField label="Checked out on" value={details.checkedOutOn} />
            ) : null}
            {hasVaultDetailValue(details.checkoutReason) ? (
              <DetailField label="Checkout reason" value={details.checkoutReason} />
            ) : null}
          </>
        ) : null}
      </dl>
    );
  };

  const renderCommentsRail = () => (
    <QmsVaultCommentsPanel
      active={railView === "comments"}
      variant="rail"
      documentId={row.DocumentId}
      userData={userData}
      staffOptions={getVaultCommentMentionCandidates(workspaceResponse || {})}
      saving={panelSaving}
      onSave={({ comment, previousCommentId, assignedStaff, commentPriority }) =>
        runCommentAction(
          () =>
            saveDocumentComment(userData, {
              DocumentId: row.DocumentId,
              Comment: comment,
              PreviousCommentId: previousCommentId || "",
              AssignedStaff: assignedStaff || "",
              CommentPriority: commentPriority,
            }),
          "Comment posted.",
        )
      }
      onEdit={({ commentId, comment, assignedStaff, commentPriority, previousCommentId }) =>
        runCommentAction(
          () =>
            editDocumentComment(userData, {
              DocumentId: row.DocumentId,
              CommentId: commentId,
              Comment: comment,
              PreviousCommentId: previousCommentId || "",
              AssignedStaff: assignedStaff || "",
              CommentPriority: commentPriority,
            }),
          "Comment updated.",
        )
      }
      onDelete={({ commentId }) =>
        runCommentAction(
          () =>
            deleteDocumentComment(userData, {
              DocumentId: row.DocumentId,
              CommentId: commentId,
            }),
          "Comment deleted.",
        )
      }
    />
  );

  const renderSecondaryPanel = () => {
    if (detailsLoading && !workspaceResponse) {
      return (
        <div className="qms-doc-workspace__panel">
          <p className="text-muted mb-0 small">Loading workspace…</p>
        </div>
      );
    }

    switch (resolvedPanel) {
      case "reviewers":
        return (
          <QmsVaultPeoplePanel
            mode="reviewers"
            workspaceResponse={workspaceResponse || {}}
            workflow={workflow}
            saving={panelSaving}
            onSave={(actionType, reviewers) =>
              runPanelAction(
                () =>
                  saveVaultReviewers(userData, {
                    documentId: row.DocumentId,
                    reviewers,
                    actionType,
                  }),
                "Reviewers updated.",
              )
            }
            onRemove={(reviewerId) =>
              runPanelAction(
                () =>
                  removeVaultReviewer(userData, {
                    documentId: row.DocumentId,
                    reviewerId,
                  }),
                "Reviewer removed.",
              )
            }
            onComplete={async () => {
              const authRes = await openAuthModal({
                showDescription: true,
                requireDescription: true,
                descriptionLabel: "Reason for completing review",
                descriptionPlaceholder: "Enter why this review cycle is complete…",
              });
              if (authRes?.Success !== 1) {
                if (!authRes?.cancelled) {
                  notifyError(null, "Authentication failed or was cancelled.");
                }
                return;
              }
              const reason = String(authRes?.Description || "").trim();
              if (!reason) {
                notifyError(null, "A reason is required to complete the review.");
                return;
              }
              return runPanelAction(
                () => vaultReviewComplete(userData, row.DocumentId, reason),
                "Review marked complete.",
              );
            }}
          />
        );
      case "approvers":
        return (
          <QmsVaultPeoplePanel
            mode="approvers"
            workspaceResponse={workspaceResponse || {}}
            workflow={workflow}
            saving={panelSaving}
            onSave={(actionType, approvers) =>
              runPanelAction(
                () =>
                  saveVaultApprovers(userData, {
                    documentId: row.DocumentId,
                    approvers,
                    actionType,
                  }),
                "Approvers updated.",
              )
            }
            onRemove={(approverId) =>
              runPanelAction(
                () =>
                  removeVaultApprover(userData, {
                    documentId: row.DocumentId,
                    approverId,
                  }),
                "Approver removed.",
              )
            }
            onComplete={async () => {
              const authRes = await openAuthModal({
                showDescription: true,
                requireDescription: true,
                descriptionLabel: "Reason for completing approval",
                descriptionPlaceholder:
                  "Enter why this approval cycle is complete…",
              });
              if (authRes?.Success !== 1) {
                if (!authRes?.cancelled) {
                  notifyError(null, "Authentication failed or was cancelled.");
                }
                return;
              }
              const reason = String(authRes?.Description || "").trim();
              if (!reason) {
                notifyError(null, "A reason is required to complete the approval.");
                return;
              }
              return runPanelAction(
                () => vaultApprovalComplete(userData, row.DocumentId, reason),
                "Approval marked complete.",
              );
            }}
          />
        );
      case "quiz":
        return (
          <QmsVaultQuizPanel
            workspaceResponse={workspaceResponse || {}}
            workflow={workflow}
            userData={userData}
            saving={panelSaving}
            onLinkQuiz={(quizId) =>
              runPanelAction(
                () => bindVaultDocumentQuiz(userData, { documentId: row.DocumentId, quizId }),
                "Quiz linked.",
              )
            }
            onUnlinkQuiz={() =>
              runPanelAction(
                () => unbindVaultDocumentQuiz(userData, row.DocumentId),
                "Quiz unlinked.",
              )
            }
          />
        );
      case "associatedDocs":
        return (
          <QmsVaultAssociatedDocsPanel
            workspaceResponse={workspaceResponse || {}}
            workflow={workflow}
            saving={panelSaving}
            onSave={(associatedDocuments) =>
              runAssociatedDocsAction(
                () =>
                  saveVaultAssociatedDocuments(userData, {
                    documentId: row.DocumentId,
                    associatedDocuments,
                  }),
                "Associated documents saved.",
              )
            }
            onRemove={(associatedDocumentId) =>
              runAssociatedDocsAction(
                () =>
                  removeVaultAssociatedDocument(userData, {
                    documentId: row.DocumentId,
                    associatedDocumentId,
                  }),
                "Associated document removed.",
              )
            }
            onPreviewDocument={openAssociatedDocPreview}
          />
        );
      case "versions":
        return (
          <QmsVaultVersionHistoryPanel
            workspaceResponse={workspaceResponse || {}}
            loading={detailsLoading && !workspaceResponse}
            onOpenVersion={(versionRow) => {
              setPreviewRow(versionRow);
              onPanelChange?.("preview");
            }}
          />
        );
      case "auditLog":
        return (
          <QmsVaultAuditLogPanel
            workspaceResponse={workspaceResponse || {}}
            loading={detailsLoading && !workspaceResponse}
          />
        );
      default:
        return null;
    }
  };

  const isPreviewPanel = resolvedPanel === "preview";

  return (
    <>
      <div
        className="modal fade show d-block qms-doc-workspace-modal qms-vault-workspace-modal qms-doc-lib-workspace-modal"
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        aria-label="My Vault document workspace"
      >
        <div className="modal-dialog qms-doc-workspace-modal__dialog">
          <div className="modal-content qms-doc-workspace-modal__content">
            <div className="qms-doc-workspace-modal__header">
              <div className="qms-doc-workspace-modal__header-main">
                <div className="qms-doc-workspace-modal__icon" aria-hidden="true">
                  <i className="fas fa-folder-open" />
                </div>
                <div>
                  <h5 className="qms-doc-workspace-modal__title">
                    {getVaultDocumentTitle(row)}
                  </h5>
                  <div className="qms-doc-workspace-modal__code-row">
                    <div className="qms-doc-workspace-modal__code">
                      {getVaultDocumentListId(row)}
                    </div>
                    {headerStage ? (
                      <span
                        className={`qms-doc-workspace-modal__stage ${getVaultStageClass(headerStage)}`}
                      >
                        {headerStage}
                      </span>
                    ) : null}
                    {viewingHistoricalVersion ? (
                      <span className="qms-vault-workspace__version-badge is-historical">
                        Preview v{previewRow?.VersionNumber ?? "—"}
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
              <div className="qms-doc-workspace-modal__header-actions">
                <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
              </div>
            </div>

            <div className="qms-doc-workspace-modal__body">
              <nav
                className="qms-doc-workspace-modal__sidebar"
                aria-label="Document workspace sections"
              >
                <ul className="qms-doc-workspace-modal__nav">
                  {VAULT_WORKSPACE_PANELS.map((panel) => {
                    const isActive = panel.id === resolvedPanel;
                    return (
                      <li key={panel.id}>
                        <button
                          type="button"
                          className={`qms-doc-workspace-modal__nav-item${
                            isActive ? " is-active" : ""
                          }`}
                          onClick={() => {
                            if (panel.id === "preview" && previewRow) {
                              returnToLatestVersion();
                              return;
                            }
                            if (panel.id !== "preview" && panel.id !== "versions") {
                              setPreviewRow(null);
                            }
                            onPanelChange?.(panel.id);
                          }}
                        >
                          <i className={panel.icon} aria-hidden="true" />
                          <span>{panel.label}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </nav>

              <div className="qms-doc-workspace-modal__main qms-vault-workspace-modal__split">
                <div
                  className={`qms-vault-workspace__panel-main${
                    resolvedPanel === "preview" ? " is-preview" : ""
                  }${
                    resolvedPanel === "associatedDocs" ? " is-associated-docs" : ""
                  }`}
                >
                  <div className="qms-vault-workspace__panel-stack">
                    <div
                      className={`qms-vault-workspace__panel-layer${
                        isPreviewPanel ? " is-active" : ""
                      }`}
                      aria-hidden={!isPreviewPanel}
                    >
                      <div
                        className="qms-doc-workspace__preview-pane"
                        data-tour="qms-vault-workspace-preview"
                      >
                        {viewingAssociatedDocument ? (
                          <div className="qms-vault-workspace__version-banner is-associated">
                            <div className="qms-vault-workspace__version-banner-copy">
                              <i className="fas fa-link" aria-hidden="true" />
                              <span>
                                Previewing associated document{" "}
                                <strong>{getVaultDocumentTitle(previewRow)}</strong>
                              </span>
                            </div>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary qms-vault-workspace__version-banner-btn"
                              onClick={returnToLatestVersion}
                            >
                              Back to main document
                            </button>
                          </div>
                        ) : viewingHistoricalVersion ? (
                          <div className="qms-vault-workspace__version-banner">
                            <div className="qms-vault-workspace__version-banner-copy">
                              <i className="fas fa-history" aria-hidden="true" />
                              <span>
                                Preview only — older version{" "}
                                <strong>v{previewRow?.VersionNumber ?? "—"}</strong>
                                {row?.VersionNumber != null ? (
                                  <>
                                    {" "}
                                    (current is <strong>v{row.VersionNumber}</strong>)
                                  </>
                                ) : null}
                                . All other workspace data uses the current version.
                              </span>
                            </div>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary qms-vault-workspace__version-banner-btn"
                              onClick={returnToLatestVersion}
                            >
                              Back to latest version
                            </button>
                          </div>
                        ) : null}
                        <div className="qms-vault-workspace__preview-body">
                          <QmsDocumentPreviewPane
                            active={isPreviewPanel}
                            documentId={
                              previewRow?.DocumentId ||
                              previewRow?.DocumentIdRef ||
                              row.DocumentId ||
                              row.DocumentIdRef ||
                              ""
                            }
                            documentUrl={previewRow?.DocumentURL || row.DocumentURL}
                            documentExtension={previewRow?.DocumentExtension || row.DocumentExtension}
                            documentTitle={getVaultDocumentTitle(previewRow || row)}
                            documentMetadata={previewDocumentMetadata}
                          />
                        </div>
                      </div>
                    </div>
                    <div
                      className={`qms-vault-workspace__panel-layer${
                        !isPreviewPanel ? " is-active" : ""
                      }`}
                      aria-hidden={isPreviewPanel}
                    >
                      <div className="qms-vault-workspace__secondary-panel">
                        {renderSecondaryPanel()}
                      </div>
                    </div>
                  </div>
                </div>

                <aside
                  className={`qms-vault-workspace__details-rail${
                    railView === "comments" ? " is-comments-mode" : ""
                  }`}
                  aria-label={railView === "comments" ? "Document comments" : "Document details"}
                >
                  <div className="qms-vault-workspace__rail-toggle" role="tablist" aria-label="Right panel">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={railView === "details"}
                      className={`qms-vault-workspace__rail-toggle-btn${
                        railView === "details" ? " is-active" : ""
                      }`}
                      onClick={() => setRailView("details")}
                    >
                      Details
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={railView === "comments"}
                      className={`qms-vault-workspace__rail-toggle-btn${
                        railView === "comments" ? " is-active" : ""
                      }`}
                      onClick={() => setRailView("comments")}
                    >
                      Comments
                    </button>
                  </div>

                  {railView === "details" ? (
                    <div className="qms-vault-workspace__rail-details-body">
                      {renderDetailsPanel()}
                    </div>
                  ) : (
                    <div className="qms-vault-workspace__rail-comments-body">
                      {renderCommentsRail()}
                    </div>
                  )}
                </aside>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  );
}

export default QmsMyVaultWorkspaceModal;
